"use client";

import type { FiveDimensionId } from "./landing-dimension-accordion";
import type { CSSProperties } from "react";

import { m, useReducedMotion } from "motion/react";
import Image from "next/image";

type LandingChartProps = {
  activeId: FiveDimensionId | null;
};

type DimensionDotProps = {
  color: string;
  active: boolean;
  shouldReduceMotion: boolean;
};

type DimensionPointConfig = {
  id: FiveDimensionId;
  label: string;
  color: string;
  top: number;
  left: number;
  labelPosition: CSSProperties;
};

const DIMENSION_POINTS: readonly DimensionPointConfig[] = [
  {
    id: "demographic",
    label: "Demographic",
    color: "#aad58b",
    top: -44,
    left: 214,
    labelPosition: { top: 0, left: -26 },
  },
  {
    id: "assets",
    label: "assets",
    color: "#ABAFE0",
    top: 144,
    left: 470,
    labelPosition: { top: 0, left: -5 },
  },
  {
    id: "influence",
    label: "influence",
    color: "#F65A5A",
    top: 446,
    left: 372,
    labelPosition: { bottom: 0, left: -26 },
  },
  {
    id: "interests",
    label: "Interests",
    color: "#FF9925",
    top: 446,
    left: 54,
    labelPosition: { bottom: 0, left: -26 },
  },
  {
    id: "knowledge",
    label: "knowledge",
    color: "#CDF970",
    top: 144,
    left: -43,
    labelPosition: { top: 0, left: -26 },
  },
];

/**
 * Dot states:
 * - inactive: static 24.607px circle (existing design)
 * - active: pulse from 24.607px to 89px (same size as the point wrapper)
 */
function DimensionDot({
  color,
  active,
  shouldReduceMotion,
}: DimensionDotProps) {
  if (!active || shouldReduceMotion) {
    return (
      <div
        className="absolute inset-0 m-auto h-[24.607px] w-[24.607px] rounded-full border border-black"
        style={{ backgroundColor: color }}
      />
    );
  }

  return (
    <div
      className="absolute inset-0 z-10 m-auto h-[89px] w-[89px]"
      style={{ "--dot-color": color } as CSSProperties}
    >
      <div className="h-full w-full">
        <svg viewBox="0 0 89 89" className="h-full w-full overflow-visible">
          <circle
            className="landing-dot-pulse landing-dot-pulse-1"
            cx="44.5"
            cy="44.5"
            r="44.5"
            style={{
              fill: color,
              transform: "scale(0)",
              transformOrigin: "44.5px 44.5px",
              opacity: 0.8,
            }}
          />
          <circle
            className="landing-dot-pulse landing-dot-pulse-2"
            cx="44.5"
            cy="44.5"
            r="44.5"
            style={{
              fill: color,
              transform: "scale(0)",
              transformOrigin: "44.5px 44.5px",
              opacity: 0.8,
            }}
          />
          <circle
            className="landing-dot-pulse landing-dot-pulse-3"
            cx="44.5"
            cy="44.5"
            r="44.5"
            style={{
              fill: color,
              transform: "scale(0)",
              transformOrigin: "44.5px 44.5px",
              opacity: 0.8,
            }}
          />
          <circle
            className="landing-dot-core"
            cx="44.5"
            cy="44.5"
            r="12.3035"
            style={{ fill: color, transformOrigin: "44.5px 44.5px" }}
          />
        </svg>
      </div>
    </div>
  );
}

export function LandingChart({ activeId }: LandingChartProps) {
  const shouldReduceMotion = useReducedMotion() ?? false;

  return (
    <div className="flex h-[668px] w-[713px] flex-col items-center justify-center">
      <m.div
        className="relative"
        initial={shouldReduceMotion ? "visible" : "hidden"}
        whileInView="visible"
        viewport={{ amount: 0.4, once: true }}
        variants={{ hidden: {}, visible: {} }}
      >
        <m.div
          variants={{
            hidden: { opacity: 0, scale: 0.95, filter: "blur(8px)" },
            visible: {
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
              transition: {
                duration: 0.62,
                delay: 0.08,
                ease: [0.16, 1, 0.3, 1],
              },
            },
          }}
        >
          <Image
            src="/images/landing/chart_bg.webp"
            alt="chart"
            width={516}
            height={495}
            sizes="516px"
          />
        </m.div>
        <m.div
          className="absolute left-[44px] top-[107px]"
          variants={{
            hidden: { opacity: 0, y: 18, filter: "blur(8px)" },
            visible: {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: {
                duration: 0.54,
                delay: 0.36,
                ease: [0.16, 1, 0.3, 1],
              },
            },
          }}
        >
          <Image
            src="/images/landing/chart_shadow.webp"
            alt="chart"
            width={431}
            height={315}
            sizes="431px"
          />
        </m.div>
        <m.div
          className="pointer-events-none absolute top-[58%] left-1/2 h-[376px] w-[299px] max-w-none -translate-x-1/2 translate-y-[-66%]"
          variants={{
            hidden: { opacity: 0, y: 20, scale: 0.78, filter: "blur(10px)" },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              filter: "blur(0px)",
              transition: {
                duration: 0.68,
                delay: 0.46,
                ease: [0.16, 1, 0.3, 1],
              },
            },
          }}
        >
          <Image
            alt=""
            width={299}
            height={376}
            sizes="299px"
            src="/images/landing/avator.webp"
          />
        </m.div>
        <m.div
          variants={{
            hidden: {},
            visible: {
              transition: {
                delayChildren: 0.72,
                staggerChildren: 0.08,
              },
            },
          }}
        >
          {DIMENSION_POINTS.map((point) => (
            <m.div
              key={`dot-${point.id}`}
              className="absolute flex h-[89px] w-[89px] items-center justify-center"
              style={{ top: point.top, left: point.left }}
              variants={{
                hidden: { opacity: 0, scale: 0.72, filter: "blur(6px)" },
                visible: {
                  opacity: 1,
                  scale: 1,
                  filter: "blur(0px)",
                  transition: { duration: 0.42, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <DimensionDot
                color={point.color}
                active={activeId === point.id}
                shouldReduceMotion={shouldReduceMotion}
              />
            </m.div>
          ))}
        </m.div>
        <m.div
          variants={{
            hidden: {},
            visible: {
              transition: {
                delayChildren: 1.02,
                staggerChildren: 0.08,
              },
            },
          }}
        >
          {DIMENSION_POINTS.map((point) => (
            <m.div
              key={`label-${point.id}`}
              style={{ top: point.top, left: point.left }}
              className="pointer-events-none absolute flex h-[89px] w-[89px] items-center justify-center"
            >
              <m.div
                style={point.labelPosition}
                className="pointer-events-none absolute whitespace-nowrap text-center text-[19.663px] leading-[26.217px] font-bold text-black uppercase"
                variants={{
                  hidden: { opacity: 0, y: 8, filter: "blur(6px)" },
                  visible: {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
              >
                {point.label}
              </m.div>
            </m.div>
          ))}
        </m.div>
      </m.div>
      <style jsx global>{`
        .landing-dot-pulse {
          fill: var(--dot-color);
          transform-origin: 44.5px 44.5px;
          transform: scale(0);
          animation: pulseOut 3s ease-out infinite;
        }

        .landing-dot-pulse-1 {
          animation-delay: 0s;
        }

        .landing-dot-pulse-2 {
          animation-delay: 1s;
        }

        .landing-dot-pulse-3 {
          animation-delay: 2s;
        }

        .landing-dot-core {
          fill: var(--dot-color);
          transform-origin: 44.5px 44.5px;
          animation: corePulse 1.5s ease-in-out infinite;
        }

        @keyframes pulseOut {
          0% {
            transform: scale(0);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        @keyframes corePulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.15);
          }
        }
      `}</style>
    </div>
  );
}
